import { useEffect, useMemo, useState } from 'react'

import { PlaybookType } from 'app/types'
import { logToAnalytics } from 'modules'
import { DEFAULT_STEP_DURATION } from './Comments'

// video progress bar zIndex is 10 so we use 11
const LAYER_INDEX = 11
const CUSTOM_CONTROLS_HEIGHT = -63
const REDUCE_METADATA_MARGIN = 8

type Props = {
    videoElement: HTMLVideoElement | null
    isVisible: boolean
    playbook: PlaybookType
    uid: string
    selectedStepId: string
    relativeTime: number
    isPlaybookQG: boolean
}

export const CommentsTimeline = ({
    videoElement,
    isVisible,
    playbook,
    uid,
    selectedStepId,
    relativeTime,
    isPlaybookQG
}: Props) => {
    const [commentsWidth, setCommentsWidth] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)

    const videoImage = useMemo(() => {
        if (!videoElement?.currentTime) return ''

        let canvas = document.createElement('canvas')

        canvas.width = videoElement.videoWidth
        canvas.height = videoElement.videoHeight

        const ctx = canvas.getContext('2d')
        ctx?.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

        return canvas.toDataURL('image/jpeg')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoElement?.currentTime, videoElement])

    // Get step image using the current video time.
    // The video time should be between the start and end time of the step.
    // The image is used in the comment annotation in the "inscreen-anchor" element
    const stepQGImage = useMemo(() => {
        if (!isPlaybookQG) return
        if (!videoElement?.currentTime) return ''

        let image = ''
        let start = 0

        playbook.steps.some(step => {
            const end = start + (step.duration || DEFAULT_STEP_DURATION)

            if (videoElement.currentTime >= start && videoElement.currentTime <= end) {
                image = step.docScreenshot || step.drawnScreenshot
                return true
            }
            start = end
            return false
        })

        return image
    }, [playbook.steps, videoElement?.currentTime, isPlaybookQG])

    // create a subset of the steps object
    const commentsSteps = useMemo(() => {
        if (!isPlaybookQG) return []
        let start = 0

        const steps = playbook.steps.map(step => {
            const end = start + (step.duration || DEFAULT_STEP_DURATION)
            const stepObj = {
                duration: step?.duration || DEFAULT_STEP_DURATION,
                id: step.id,
                left: (start / playbook.duration) * 100,
                width: ((step.duration || DEFAULT_STEP_DURATION) / playbook.duration) * 100
            }
            start = end
            return stepObj
        })

        return steps
    }, [playbook.duration, playbook.steps, isPlaybookQG])

    const anchorLocator = useMemo(() => {
        return isPlaybookQG
            ? `${selectedStepId}::${relativeTime}`
            : `${Math.floor(currentTime * 1000)}`
    }, [isPlaybookQG, relativeTime, selectedStepId, currentTime])

    useEffect(() => {
        const onTimeUpdate = () => {
            setCurrentTime(videoElement?.currentTime || 0)
        }

        const handlePosition = () => {
            const { width = 0 } = videoElement?.getBoundingClientRect() || {}

            setCommentsWidth(width + window.scrollX)
        }

        handlePosition()

        window.addEventListener('resize', handlePosition)
        videoElement?.addEventListener('loadeddata', handlePosition)
        videoElement?.addEventListener('timeupdate', onTimeUpdate)

        return () => {
            window.removeEventListener('resize', handlePosition)
            videoElement?.removeEventListener('loadeddata', handlePosition)
            videoElement?.removeEventListener('timeupdate', onTimeUpdate)
        }
    }, [videoElement])

    useEffect(() => {
        const listener = (ev: any) => {
            const event = {
                eventName: '',
                user: uid,
                playbookId: playbook.id,
                timeOffset: 0
            }

            switch (ev.detail.name) {
                case 'threadCreated':
                    event.eventName = 'Add comment'
                    break
                case 'replyAdded':
                    event.eventName = 'Reply comment'
                    break
                case 'timelineIndicatorActivated':
                    event.eventName = 'Open comment'
                    break
                case 'threadResolved':
                    event.eventName = 'Resolve comment'
                    break
                default:
                    break
            }
            event.timeOffset = videoElement?.currentTime || 0

            if (Boolean(event.eventName)) {
                logToAnalytics('Comments', event)
            }
        }

        //Fix sentry page crash
        let timelineHandler: () => void

        window?.inScreen?.queueForPostInitialization(() => {
            window.inScreen.addClientEventsListener(listener)
            timelineHandler = window.inScreen.listenToTimelineActivations(({ locator, value }) => {
                if (locator.startsWith(`my-video::${selectedStepId}::`)) {
                    return false // inScreen: This prevents confusions between two video players.
                }

                // my-video::[playbookId]::[stepId]
                // locator example: my-video::xgBZaDAwjcRzB7i5ofXwJo::tuxY91QwvrCcsPTqzs3ypj
                const commentStepId = locator.split('::')[2]

                if (videoElement) {
                    if (isPlaybookQG) {
                        let start = 0
                        // find the step
                        playbook.steps.some(step => {
                            if (step.id === commentStepId) {
                                // seek QG time
                                videoElement.currentTime = start + value / 1000
                                return true
                            }
                            start += step.duration || DEFAULT_STEP_DURATION
                            return false
                        })
                    } else {
                        // seek video time
                        videoElement.currentTime = value / 1000
                    }

                    videoElement?.pause()
                }

                return true // <- inScreen mandatory
            })
        })

        return () => {
            window?.inScreen?.queueForPostInitialization(() => {
                window.inScreen.removeClientEventsListener(listener)
            })
            timelineHandler?.()
        }
    }, [
        playbook.id,
        uid,
        videoElement?.currentTime,
        playbook.steps,
        selectedStepId,
        videoElement,
        isPlaybookQG
    ])

    if (!isVisible) return null

    return (
        <>
            {isPlaybookQG ? (
                commentsSteps.map(step => (
                    <inscreen-inline-timeline-threads-display
                        key={step.id}
                        locator={`my-video::${playbook.id}::${step.id}`}
                        start-value={`0`}
                        end-value={`${step.duration * 1000}`}
                        style={{
                            position: 'absolute',
                            '--inscreen-base-z-index': LAYER_INDEX,
                            top: `${CUSTOM_CONTROLS_HEIGHT}px`,
                            width: `${step.width}%`,
                            left: `calc(${step.left}% - ${REDUCE_METADATA_MARGIN}px)`
                        }}
                    />
                ))
            ) : (
                <inscreen-inline-timeline-threads-display
                    locator={`my-video::${playbook.id}`}
                    start-value="0"
                    end-value={`${playbook.duration * 1000}`}
                    style={{
                        position: 'absolute',
                        '--inscreen-base-z-index': LAYER_INDEX,
                        top: `${CUSTOM_CONTROLS_HEIGHT}px`,
                        width: commentsWidth
                    }}
                />
            )}

            <inscreen-anchor
                version="A"
                locator={`my-video::${playbook.id}::${anchorLocator}`}
                screenshot-url={isPlaybookQG ? stepQGImage : videoImage}
                behavior="inline-timeline-threads-display"
            />
        </>
    )
}
